import { useRef, useEffect, useImperativeHandle, forwardRef } from 'react'
import { getStyledColor, getPaintBehavior } from './utils/colorStyles'
import { drawRoughFlowShape, drawRoughFlowText } from './utils/roughFlowDraw'

function getOutlinePixel(outlineData, width, x, y) {
  if (!outlineData || x < 0 || x >= width || y < 0 || y >= outlineData.height) return 255
  const i = (y * width + x) * 4
  const r = outlineData.data[i]
  const g = outlineData.data[i + 1]
  const b = outlineData.data[i + 2]
  return (r + g + b) / 3
}

function hexToRgb(hex) {
  const n = hex.replace('#', '')
  if (n.length !== 6) return null
  return [
    parseInt(n.slice(0, 2), 16),
    parseInt(n.slice(2, 4), 16),
    parseInt(n.slice(4, 6), 16),
  ]
}

function rgbToHex(r, g, b) {
  const toHex = (x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function floodFill(ctx, startX, startY, fillColorHex, outlineData) {
  const width = ctx.canvas.width
  const height = ctx.canvas.height
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  const getPixel = (x, y) => {
    if (x < 0 || x >= width || y < 0 || y >= height) return null
    const i = (y * width + x) * 4
    return [data[i], data[i + 1], data[i + 2], data[i + 3]]
  }

  const setPixel = (x, y, r, g, b, a) => {
    if (x < 0 || x >= width || y < 0 || y >= height) return
    const i = (y * width + x) * 4
    data[i] = r
    data[i + 1] = g
    data[i + 2] = b
    data[i + 3] = a
  }

  const isOutline = (x, y) => {
    const lum = getOutlinePixel(outlineData, width, x, y)
    return lum < 80
  }

  const colorEq = (a, b) => a && b && a[0] === b[0] && a[1] === b[1] && a[2] === b[2]

  const fillRgb = hexToRgb(fillColorHex)
  if (!fillRgb) return

  const startXf = Math.floor(startX)
  const startYf = Math.floor(startY)
  if (isOutline(startXf, startYf)) return
  const start = getPixel(startXf, startYf)
  if (!start) return
  if (colorEq(start, fillRgb)) return

  const stack = [[startXf, startYf]]
  const seen = new Set()
  seen.add(`${startXf},${startYf}`)
  const maxPixels = width * height
  let count = 0

  while (stack.length && count < maxPixels) {
    const [x, y] = stack.pop()
    count++
    setPixel(x, y, fillRgb[0], fillRgb[1], fillRgb[2], 255)

    const neighbors = [
      [x + 1, y],
      [x - 1, y],
      [x, y + 1],
      [x, y - 1],
    ]
    for (const [nx, ny] of neighbors) {
      const key = `${nx},${ny}`
      if (seen.has(key)) continue
      if (isOutline(nx, ny)) continue
      const p = getPixel(nx, ny)
      if (!p || !colorEq(p, start)) continue
      seen.add(key)
      stack.push([nx, ny])
    }
  }

  ctx.putImageData(imageData, 0, 0)
}

/**
 * ממלא את כל פיקסלי ה"דף" (לבן/בהיר) בצבע הנבחר, בלי לגעת בקווי מתאר או בצבעים כהים שכבר על הקנבס.
 * @param {HTMLCanvasElement} canvas
 * @param {string} fillColorHex
 * @param {ImageData|null} outlineData - ממצב קווים נפרדים; אם null, מזהים קווים לפי בהירות הפיקסל בקנבס
 * @param {number} paperThreshold - 220–255: ככל שהערך נמוך יותר, גווני רקע בהירים יותר ייחשבו "דף" ויימלאו
 */
export function fillCanvasPaperBackground(canvas, fillColorHex, outlineData, paperThreshold = 248) {
  if (!canvas?.width || !canvas?.height) return
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return
  const width = canvas.width
  const height = canvas.height
  const t = Math.min(255, Math.max(220, Math.round(paperThreshold)))
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data
  const fillRgb = hexToRgb(fillColorHex)
  if (!fillRgb) return

  const outlineOk =
    outlineData &&
    outlineData.width === width &&
    outlineData.height === height &&
    outlineData.data?.length >= width * height * 4

  const isProtectedLine = (x, y) => {
    if (outlineOk) {
      const lum = getOutlinePixel(outlineData, width, x, y)
      return lum < 80
    }
    const i = (y * width + x) * 4
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    return (r + g + b) / 3 < 88
  }

  const isPaperPixel = (x, y) => {
    const i = (y * width + x) * 4
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    return r >= t && g >= t && b >= t
  }

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (isProtectedLine(x, y)) continue
      if (!isPaperPixel(x, y)) continue
      const i = (y * width + x) * 4
      data[i] = fillRgb[0]
      data[i + 1] = fillRgb[1]
      data[i + 2] = fillRgb[2]
      data[i + 3] = 255
    }
  }
  ctx.putImageData(imageData, 0, 0)
}

/** זריקת צבע על הקנבס — כתמים אקראיים (מכבד קווי מתאר אם קיימים) */
function drawPaintSplash(ctx, cx, cy, colorHex, brushSize, outlineData) {
  const rgb = hexToRgb(colorHex)
  if (!rgb) return
  const w = ctx.canvas.width
  const h = ctx.canvas.height
  const baseR = Math.max(22, brushSize * 2.4)

  const outlineOk =
    outlineData &&
    outlineData.width === w &&
    outlineData.height === h &&
    outlineData.data?.length >= w * h * 4

  const centerClear = (x, y) => {
    if (x < 0 || x >= w || y < 0 || y >= h) return false
    if (!outlineOk) return true
    return getOutlinePixel(outlineData, w, x, y) >= 80
  }

  const nMain = 11 + Math.floor(Math.random() * 7)
  const nDrip = 7 + Math.floor(Math.random() * 7)

  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  for (let i = 0; i < nMain; i++) {
    const ang = Math.random() * Math.PI * 2
    const dist = Math.random() * baseR * 0.9
    const x = cx + Math.cos(ang) * dist
    const y = cy + Math.sin(ang) * dist
    const rx = baseR * (0.12 + Math.random() * 0.5)
    const ry = baseR * (0.1 + Math.random() * 0.45)
    if (!centerClear(Math.floor(x), Math.floor(y))) continue
    ctx.fillStyle = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${0.62 + Math.random() * 0.36})`
    ctx.beginPath()
    ctx.ellipse(x, y, rx, ry, Math.random() * Math.PI, 0, Math.PI * 2)
    ctx.fill()
  }
  for (let i = 0; i < nDrip; i++) {
    const ang = Math.random() * Math.PI * 2
    const dist = baseR * (0.45 + Math.random() * 1.35)
    const x = cx + Math.cos(ang) * dist
    const y = cy + Math.sin(ang) * dist
    const r = baseR * (0.04 + Math.random() * 0.22)
    if (!centerClear(Math.floor(x), Math.floor(y))) continue
    ctx.fillStyle = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${0.48 + Math.random() * 0.47})`
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

/**
 * נצנצים אמיתיים: פתיתים קטנים (מלבנים + יהלומים) עם הברק (highlight), גודל וזווית אקראיים
 */
function drawGlitter(ctx, x, y, colorHex, intensity, radius) {
  const rgb = hexToRgb(colorHex)
  if (!rgb) return
  const count = Math.max(10, Math.floor(radius * intensity * 5))
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2
    const r = Math.random() * radius
    const px = x + Math.cos(angle) * r
    const py = y + Math.sin(angle) * r
    const rot = Math.random() * Math.PI * 2
    const w = 1.2 + Math.random() * 2.8
    const h = 0.35 + Math.random() * 0.9
    const alpha = 0.55 + Math.random() * 0.45
    const isDiamond = Math.random() > 0.55

    ctx.save()
    ctx.translate(px, py)
    ctx.rotate(rot)

    // פתית – מלבן או יהלום (4 קודקודים)
    ctx.fillStyle = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`
    ctx.beginPath()
    if (isDiamond) {
      ctx.moveTo(0, -h / 2)
      ctx.lineTo(w / 2, 0)
      ctx.lineTo(0, h / 2)
      ctx.lineTo(-w / 2, 0)
      ctx.closePath()
    } else {
      ctx.rect(-w / 2, -h / 2, w, h)
    }
    ctx.fill()

    // הברק – נקודת אור על הפתית (כמו פן של נצנצים אמיתיים)
    const hx = (Math.random() - 0.5) * w * 0.5
    const hy = (Math.random() - 0.5) * h * 0.5
    const hs = 0.35 + Math.random() * 0.55
    ctx.fillStyle = `rgba(255,255,255,${0.7 + Math.random() * 0.3})`
    ctx.beginPath()
    ctx.arc(hx, hy, hs, 0, Math.PI * 2)
    ctx.fill()

    // נקודת זוהר שנייה בחלק מהפתיתים
    if (Math.random() > 0.55) {
      const lx = (Math.random() - 0.5) * w * 0.8
      const ly = (Math.random() - 0.5) * h * 0.8
      ctx.fillStyle = `rgba(255,255,255,${0.25 + Math.random() * 0.35})`
      ctx.beginPath()
      ctx.arc(lx, ly, 0.2 + Math.random() * 0.2, 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.restore()
  }
}

const ColoringCanvas = forwardRef(function ColoringCanvas(
  {
    outlineSize,
    outlineDataRef,
    eraserBaseImageUrl = null,
    currentColor,
    paintStyle = 'normal',
    brushSize,
    tool,
    glitterIntensity,
    stampChar,
    stampFontFamily = "'Heebo', -apple-system, system-ui, sans-serif",
    stickerEmoji,
    onUndoPush,
    onStrokeEnd,
    onPickColor,
    onMeasurePoint,
    lineAssistEnabled = false,
    lineAssistStrength = 0.72,
    splashToolEnabled = false,
    onSplashFx,
    flowShape = 'arrow',
    onFlowTextRequest,
  },
  ref
) {
  const canvasRef = useRef(null)
  const eraserBaseCanvasRef = useRef(null)
  const isDrawing = useRef(false)
  const lastPoint = useRef(null)
  const flowSnapshotRef = useRef(null)
  const flowStartRef = useRef(null)
  const flowSeedRef = useRef(0)
  const flowLastCoordsRef = useRef(null)

  const effectiveColor = getStyledColor(currentColor, paintStyle) || currentColor
  const paintBehavior = getPaintBehavior(paintStyle)

  useImperativeHandle(ref, () => canvasRef.current, [])

  const getCtx = (canvas) => canvas.getContext('2d', { willReadFrequently: true })

  useEffect(() => {
    if (!outlineSize.w || !outlineSize.h || !canvasRef.current) return
    const canvas = canvasRef.current
    canvas.width = outlineSize.w
    canvas.height = outlineSize.h
    const ctx = getCtx(canvas)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, outlineSize.w, outlineSize.h)
  }, [outlineSize.w, outlineSize.h])

  useEffect(() => {
    if (!outlineSize.w || !outlineSize.h) return
    const baseCanvas = document.createElement('canvas')
    baseCanvas.width = outlineSize.w
    baseCanvas.height = outlineSize.h
    const baseCtx = baseCanvas.getContext('2d', { willReadFrequently: true })
    if (!baseCtx) return
    baseCtx.fillStyle = '#ffffff'
    baseCtx.fillRect(0, 0, outlineSize.w, outlineSize.h)

    if (!eraserBaseImageUrl) {
      eraserBaseCanvasRef.current = baseCanvas
      return
    }

    const img = new Image()
    img.onload = () => {
      baseCtx.clearRect(0, 0, outlineSize.w, outlineSize.h)
      baseCtx.drawImage(img, 0, 0, outlineSize.w, outlineSize.h)
      eraserBaseCanvasRef.current = baseCanvas
    }
    img.onerror = () => {
      eraserBaseCanvasRef.current = baseCanvas
    }
    img.src = eraserBaseImageUrl
  }, [eraserBaseImageUrl, outlineSize.w, outlineSize.h])

  const getLocalCoords = (e) => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const touch = e.touches && e.touches[0] ? e.touches[0] : e.changedTouches && e.changedTouches[0] ? e.changedTouches[0] : null
    const clientX = touch ? touch.clientX : e.clientX
    const clientY = touch ? touch.clientY : e.clientY
    if (clientX == null || clientY == null) return null
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    }
  }

  const drawBrush = (ctx, x, y, prev) => {
    const size = brushSize * paintBehavior.brushSpread
    ctx.save()
    ctx.globalAlpha = paintBehavior.brushAlpha
    ctx.globalCompositeOperation = paintBehavior.compositeOperation
    ctx.strokeStyle = effectiveColor
    ctx.lineWidth = size
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    if (prev) {
      ctx.moveTo(prev.x, prev.y)
      ctx.lineTo(x, y)
    } else {
      ctx.arc(x, y, size / 2, 0, Math.PI * 2)
      ctx.fillStyle = effectiveColor
      ctx.fill()
      ctx.restore()
      return
    }
    ctx.stroke()
    ctx.restore()
  }

  const applyLineAssist = (coords) => {
    if (!lineAssistEnabled || !lastPoint.current) return coords
    const t = Math.max(0.1, Math.min(0.92, lineAssistStrength))
    return {
      x: lastPoint.current.x + (coords.x - lastPoint.current.x) * (1 - t),
      y: lastPoint.current.y + (coords.y - lastPoint.current.y) * (1 - t),
    }
  }

  /** גומייה: ממלאה בלבן כמו רקע הדף (לא נוגעת בשכבת הקווים מעל) */
  const drawEraser = (ctx, x, y, prev) => {
    const size = Math.max(4, brushSize * paintBehavior.brushSpread)
    const restoreCanvas = eraserBaseCanvasRef.current
    const stampRestore = (px, py) => {
      if (restoreCanvas) {
        ctx.save()
        ctx.beginPath()
        ctx.arc(px, py, size / 2, 0, Math.PI * 2)
        ctx.clip()
        ctx.drawImage(restoreCanvas, 0, 0)
        ctx.restore()
        return
      }
      ctx.save()
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      ctx.arc(px, py, size / 2, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }

    if (!prev) {
      stampRestore(x, y)
      return
    }

    const dx = x - prev.x
    const dy = y - prev.y
    const dist = Math.hypot(dx, dy)
    const step = Math.max(1, size * 0.35)
    const count = Math.max(1, Math.ceil(dist / step))
    for (let i = 0; i <= count; i++) {
      const t = i / count
      const px = prev.x + dx * t
      const py = prev.y + dy * t
      stampRestore(px, py)
    }
  }

  // עיפרון — קו דק ומדויק (מקצועי)
  const drawPencil = (ctx, x, y, prev) => {
    const w = Math.max(1, brushSize * 0.35)
    ctx.strokeStyle = effectiveColor
    ctx.lineWidth = w
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.globalCompositeOperation = 'source-over'
    ctx.beginPath()
    if (prev) {
      ctx.moveTo(prev.x, prev.y)
      ctx.lineTo(x, y)
    } else {
      ctx.arc(x, y, w / 2, 0, Math.PI * 2)
      ctx.fillStyle = effectiveColor
      ctx.fill()
      return
    }
    ctx.stroke()
  }

  // מרקר — צבע שקוף, רך (מקצועי)
  const drawMarker = (ctx, x, y, prev) => {
    ctx.save()
    ctx.globalAlpha = 0.45
    const w = brushSize * 1.4
    ctx.strokeStyle = effectiveColor
    ctx.lineWidth = w
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.globalCompositeOperation = 'source-over'
    ctx.beginPath()
    if (prev) {
      ctx.moveTo(prev.x, prev.y)
      ctx.lineTo(x, y)
    } else {
      ctx.arc(x, y, w / 2, 0, Math.PI * 2)
      ctx.fillStyle = effectiveColor
      ctx.fill()
      ctx.restore()
      return
    }
    ctx.stroke()
    ctx.restore()
  }

  const drawStamp = (ctx, x, y) => {
    if (!stampChar) return
    const size = brushSize * 2.2
    ctx.save()
    ctx.fillStyle = effectiveColor
    ctx.font = `${Math.max(18, size)}px ${stampFontFamily}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.globalCompositeOperation = 'source-over'
    ctx.fillText(stampChar, x, y)
    ctx.restore()
  }

  const drawSticker = (ctx, x, y) => {
    if (!stickerEmoji) return
    const size = 56
    ctx.save()
    ctx.font = `${size}px 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.globalCompositeOperation = 'source-over'
    ctx.fillText(stickerEmoji, x, y)
    ctx.restore()
  }

  // מריחה — רגישות לפי מדיה: שמן מתערבב חזק, פסטל נמרח רחוק, צבע מים רך
  const drawSmudge = (ctx, x, y, prev) => {
    if (!prev) return
    const dx = x - prev.x
    const dy = y - prev.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist < 0.5) return
    const width = ctx.canvas.width
    const height = ctx.canvas.height
    const imageData = ctx.getImageData(0, 0, width, height)
    const data = imageData.data
    const pullDist = paintBehavior.smudgePull
    const strength = paintBehavior.smudgeStrength
    const pullX = (prev.x - x) / dist * pullDist
    const pullY = (prev.y - y) / dist * pullDist
    const radius = Math.max(4, brushSize * paintBehavior.smudgeRadiusFactor)
    const radiusSq = radius * radius
    const x0 = Math.floor(x - radius)
    const x1 = Math.ceil(x + radius)
    const y0 = Math.floor(y - radius)
    const y1 = Math.ceil(y + radius)
    for (let py = y0; py <= y1; py++) {
      for (let px = x0; px <= x1; px++) {
        const ddx = px - x
        const ddy = py - y
        if (ddx * ddx + ddy * ddy > radiusSq) continue
        if (px < 0 || px >= width || py < 0 || py >= height) continue
        const sx = Math.round(px + pullX)
        const sy = Math.round(py + pullY)
        const i = (py * width + px) * 4
        let r = data[i]
        let g = data[i + 1]
        let b = data[i + 2]
        let a = data[i + 3]
        if (sx >= 0 && sx < width && sy >= 0 && sy < height) {
          const si = (sy * width + sx) * 4
          const sr = data[si]
          const sg = data[si + 1]
          const sb = data[si + 2]
          const sa = data[si + 3]
          r = (1 - strength) * r + strength * sr
          g = (1 - strength) * g + strength * sg
          b = (1 - strength) * b + strength * sb
          a = (1 - strength) * a + strength * sa
        }
        data[i] = Math.max(0, Math.min(255, r))
        data[i + 1] = Math.max(0, Math.min(255, g))
        data[i + 2] = Math.max(0, Math.min(255, b))
        data[i + 3] = Math.max(0, Math.min(255, a))
      }
    }
    ctx.putImageData(imageData, 0, 0)
  }

  const handleStart = (e) => {
    e.preventDefault()
    const coords = getLocalCoords(e)
    if (!coords || !canvasRef.current) return
    const ctx = getCtx(canvasRef.current)

    if (tool === 'flow') {
      if (flowShape === 'erase') {
        onUndoPush?.(canvasRef.current.toDataURL('image/png'))
        isDrawing.current = true
        lastPoint.current = coords
        drawEraser(ctx, coords.x, coords.y, null)
        return
      }
      if (flowShape === 'text') {
        flowStartRef.current = { x: coords.x, y: coords.y }
        flowLastCoordsRef.current = { x: coords.x, y: coords.y }
        flowSeedRef.current = (Date.now() ^ (Math.floor(coords.x * 997) + Math.floor(coords.y * 991))) >>> 0
        isDrawing.current = true
        return
      }
      onUndoPush?.(canvasRef.current.toDataURL('image/png'))
      const w = canvasRef.current.width
      const h = canvasRef.current.height
      try {
        flowSnapshotRef.current = ctx.getImageData(0, 0, w, h)
      } catch {
        flowSnapshotRef.current = null
      }
      flowStartRef.current = { x: coords.x, y: coords.y }
      flowSeedRef.current = (Date.now() ^ (Math.floor(coords.x * 997) + Math.floor(coords.y * 991))) >>> 0
      flowLastCoordsRef.current = { x: coords.x, y: coords.y }
      isDrawing.current = true
      return
    }

    const adjustedCoords = applyLineAssist(coords)

    if (tool === 'eyedropper') {
      const width = canvasRef.current.width
      const height = canvasRef.current.height
      const px = Math.floor(coords.x)
      const py = Math.floor(coords.y)
      if (px >= 0 && px < width && py >= 0 && py < height) {
        const imageData = ctx.getImageData(px, py, 1, 1)
        const [r, g, b] = imageData.data
        onPickColor?.(rgbToHex(r, g, b))
      }
      return
    }

    if (tool === 'fill') {
      onUndoPush?.(canvasRef.current.toDataURL('image/png'))
      floodFill(ctx, coords.x, coords.y, effectiveColor, outlineDataRef?.current)
      return
    }

    if (tool === 'measure') {
      onMeasurePoint?.(coords)
      return
    }

    if (tool === 'glitter') {
      onUndoPush?.(canvasRef.current.toDataURL('image/png'))
      drawGlitter(ctx, coords.x, coords.y, effectiveColor, glitterIntensity, brushSize)
      isDrawing.current = true
      lastPoint.current = coords
      return
    }

    if (tool === 'splash') {
      if (!splashToolEnabled) return
      onUndoPush?.(canvasRef.current.toDataURL('image/png'))
      drawPaintSplash(ctx, coords.x, coords.y, effectiveColor, brushSize, outlineDataRef?.current)
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY
      onSplashFx?.({ x: clientX, y: clientY, color: effectiveColor })
      onStrokeEnd?.()
      return
    }

    if (tool === 'stamp') {
      if (!stampChar) return
      onUndoPush?.(canvasRef.current.toDataURL('image/png'))
      drawStamp(ctx, coords.x, coords.y)
      return
    }

    if (tool === 'sticker') {
      if (!stickerEmoji) return
      onUndoPush?.(canvasRef.current.toDataURL('image/png'))
      drawSticker(ctx, coords.x, coords.y)
      return
    }

    if (tool === 'smudge') {
      onUndoPush?.(canvasRef.current.toDataURL('image/png'))
      isDrawing.current = true
      lastPoint.current = coords
      return
    }

    if (tool === 'eraser') {
      onUndoPush?.(canvasRef.current.toDataURL('image/png'))
      isDrawing.current = true
      lastPoint.current = coords
      drawEraser(ctx, coords.x, coords.y, null)
      return
    }

    // brush | pencil | marker
    onUndoPush?.(canvasRef.current.toDataURL('image/png'))
    isDrawing.current = true
    lastPoint.current = coords
    if (tool === 'pencil') drawPencil(ctx, coords.x, coords.y, null)
    else if (tool === 'marker') drawMarker(ctx, coords.x, coords.y, null)
    else drawBrush(ctx, coords.x, coords.y, null)
  }

  const handleMove = (e) => {
    e.preventDefault()
    if (!isDrawing.current) return
    const coords = getLocalCoords(e)
    if (!coords || !canvasRef.current) return

    if (tool === 'flow') {
      if (flowShape === 'erase') {
        const ctx = getCtx(canvasRef.current)
        drawEraser(ctx, coords.x, coords.y, lastPoint.current)
        lastPoint.current = coords
        return
      }
      if (flowShape === 'text') return
      if (!flowSnapshotRef.current || !flowStartRef.current) return
      flowLastCoordsRef.current = { x: coords.x, y: coords.y }
      const ctx = getCtx(canvasRef.current)
      const w = canvasRef.current.width
      const h = canvasRef.current.height
      ctx.putImageData(flowSnapshotRef.current, 0, 0)
      drawRoughFlowShape(ctx, flowShape, flowStartRef.current.x, flowStartRef.current.y, coords.x, coords.y, {
        color: effectiveColor,
        lineWidth: Math.max(2, brushSize * 0.42),
        seed: flowSeedRef.current,
      })
      return
    }

    const adjustedCoords = applyLineAssist(coords)
    const ctx = getCtx(canvasRef.current)

    if (tool === 'glitter') {
      drawGlitter(ctx, adjustedCoords.x, adjustedCoords.y, effectiveColor, glitterIntensity, brushSize)
      lastPoint.current = adjustedCoords
      return
    }

    if (tool === 'smudge') {
      drawSmudge(ctx, adjustedCoords.x, adjustedCoords.y, lastPoint.current)
      lastPoint.current = adjustedCoords
      return
    }

    if (tool === 'eraser') {
      drawEraser(ctx, adjustedCoords.x, adjustedCoords.y, lastPoint.current)
      lastPoint.current = adjustedCoords
      return
    }

    if (tool === 'pencil') drawPencil(ctx, adjustedCoords.x, adjustedCoords.y, lastPoint.current)
    else if (tool === 'marker') drawMarker(ctx, adjustedCoords.x, adjustedCoords.y, lastPoint.current)
    else drawBrush(ctx, adjustedCoords.x, adjustedCoords.y, lastPoint.current)
    lastPoint.current = adjustedCoords
  }

  const handleEnd = (e) => {
    e.preventDefault()
    if (tool === 'flow') {
      const canvas = canvasRef.current
      const endCoords = getLocalCoords(e) || flowLastCoordsRef.current

      if (flowShape === 'erase') {
        if (isDrawing.current && typeof onStrokeEnd === 'function') onStrokeEnd()
        isDrawing.current = false
        lastPoint.current = null
        return
      }

      if (flowShape === 'text') {
        if (isDrawing.current && canvas && flowStartRef.current && endCoords) {
          const dx = endCoords.x - flowStartRef.current.x
          const dy = endCoords.y - flowStartRef.current.y
          if (Math.hypot(dx, dy) < 14) {
            const raw =
              typeof onFlowTextRequest === 'function'
                ? onFlowTextRequest()
                : window.prompt('טקסט בתרשים:', '')
            const text = raw != null && String(raw).trim() ? String(raw).trim() : null
            if (text) {
              onUndoPush?.(canvas.toDataURL('image/png'))
              const ctx = getCtx(canvas)
              const fs = Math.max(14, Math.min(56, Math.round(brushSize * 1.35)))
              drawRoughFlowText(ctx, text, flowStartRef.current.x, flowStartRef.current.y, {
                color: effectiveColor,
                fontSize: fs,
                seed: flowSeedRef.current,
              })
              if (typeof onStrokeEnd === 'function') onStrokeEnd()
            }
          }
        }
        flowStartRef.current = null
        flowLastCoordsRef.current = null
        isDrawing.current = false
        return
      }

      if (isDrawing.current && flowSnapshotRef.current && flowStartRef.current && canvas && endCoords) {
        const ctx = getCtx(canvas)
        ctx.putImageData(flowSnapshotRef.current, 0, 0)
        const dx = endCoords.x - flowStartRef.current.x
        const dy = endCoords.y - flowStartRef.current.y
        if (Math.hypot(dx, dy) >= 3) {
          drawRoughFlowShape(ctx, flowShape, flowStartRef.current.x, flowStartRef.current.y, endCoords.x, endCoords.y, {
            color: effectiveColor,
            lineWidth: Math.max(2, brushSize * 0.42),
            seed: flowSeedRef.current,
          })
        }
        if (typeof onStrokeEnd === 'function') onStrokeEnd()
      }
      flowSnapshotRef.current = null
      flowStartRef.current = null
      flowLastCoordsRef.current = null
      isDrawing.current = false
      lastPoint.current = null
      return
    }

    if (isDrawing.current && typeof onStrokeEnd === 'function') onStrokeEnd()
    isDrawing.current = false
    lastPoint.current = null
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = getCtx(canvas)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
  }, [])

  const brushCursor = "url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2232%22 height=%2232%22 viewBox=%220 0 32 32%22%3E%3Cellipse cx=%2216%22 cy=%2222%22 rx=%225%22 ry=%224%22 fill=%22%23000%22/%3E%3Cpath fill=%22%23000%22 d=%22M14 6v16h4V6l-2-2-2 2z%22/%3E%3C/svg%3E') 16 6, crosshair"
  const pencilCursor = "url('data:image/svg+xml," + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">' +
    '<path fill="#f0c674" stroke="#c9a227" stroke-width="0.7" d="M12 4 L20 4 L22 24 L10 24 Z"/>' +
    '<path fill="#6b6b6b" d="M10 24 L16 30 L22 24 Z"/>' +
    '<path fill="#e8a0a0" stroke="#c08080" stroke-width="0.5" d="M12 4 L14 0 L18 0 L20 4 Z"/>' +
    '</svg>'
  ) + "') 16 30, crosshair"
  const canvasCursor =
    tool === 'fill' || tool === 'splash' || tool === 'measure' || (tool === 'flow' && flowShape !== 'erase')
      ? 'crosshair'
      : tool === 'flow' && flowShape === 'erase'
        ? brushCursor
      : tool === 'sticker'
        ? 'crosshair'
        : tool === 'eyedropper'
          ? 'crosshair'
          : tool === 'eraser'
            ? brushCursor
            : tool === 'pencil'
              ? pencilCursor
              : brushCursor

  return (
    <canvas
      ref={canvasRef}
      className="coloring-canvas"
      onMouseDown={handleStart}
      onMouseMove={handleMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
      style={{ touchAction: 'none', cursor: canvasCursor }}
    />
  )
})

export { ColoringCanvas }
